import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

import Header from '../components/header/Header';
import Footer from '../components/footer/Footer';

/**
 * MyHistoryStudio — structured walkthrough of the My History Studio product.
 *
 * Adapted from the standalone experience page in the vault. The original's own
 * topbar, scroll-progress bar and footer were removed so the site's Header and
 * Footer provide the page chrome instead.
 *
 * Images live in public/mhs/.
 */

/**
 * Mailchimp audience embed.
 *
 * `baseUrl` and `u` come from the `action` URL of Mailchimp's embedded form
 * (Audience → Signup forms → Embedded form).
 * `listId` is the Audience ID (Audience → Settings → Audience name and defaults).
 * `pathField` is the merge tag of the custom audience field that stores the
 * estimator answer (Audience → Settings → Audience fields and *|MERGE|* tags).
 *
 * Note: Mailchimp silently ignores merge fields that do not exist on the audience,
 * so if pathField is ever wrong the contact is still created but the estimator
 * answer is dropped without an error.
 */
const MC = {
  // Account-specific host from the embedded form's `action` URL, e.g.
  // https://kochfoto.us5.list-manage.com — NOT the bare us5.list-manage.com used
  // by the contact-form link in components/layouts/Action.jsx.
  baseUrl: 'https://kochfoto.us5.list-manage.com',
  u: '681f314a9456ece504da69f44',
  listId: '4b174bd11b',
  nameField: 'FNAME',
  pathField: 'ESTIMATOR', // custom audience field; Mailchimp also exposes it as MERGE9
};

/**
 * Submits to Mailchimp via JSONP. Mailchimp blocks cross-origin fetch/XHR, but its
 * post-json endpoint accepts a callback, so this works with no backend and without
 * navigating the visitor away from the page.
 */
function subscribeToMailchimp({ email, name, path }) {
  return new Promise((resolve, reject) => {
    if (!MC.baseUrl || !MC.listId) { reject(new Error('This form is not connected yet.')); return; }

    const cb = 'mc_cb_' + Date.now() + '_' + Math.floor(Math.random() * 1e6);
    const params = new URLSearchParams({ u: MC.u, id: MC.listId, EMAIL: email, c: cb });
    if (name) params.append(MC.nameField, name);
    if (path) params.append(MC.pathField, path);
    params.append(`b_${MC.u}_${MC.listId}`, ''); // Mailchimp's bot-trap field, must be empty

    const script = document.createElement('script');
    const timer = setTimeout(() => { cleanup(); reject(new Error('The request timed out. Please try again.')); }, 10000);

    function cleanup() {
      clearTimeout(timer);
      delete window[cb];
      if (script.parentNode) script.parentNode.removeChild(script);
    }

    window[cb] = (res) => {
      cleanup();
      if (res && res.result === 'success') { resolve(res); return; }
      const msg = (res && res.msg) || 'Something went wrong. Please try again.';
      // Mailchimp reports an existing subscriber as an error; for a lead form that is fine.
      if (/already subscribed/i.test(msg)) { resolve({ result: 'success', already: true }); return; }
      reject(new Error(msg.replace(/<[^>]*>/g, '')));
    };

    script.onerror = () => { cleanup(); reject(new Error('Could not reach Mailchimp. Please try again.')); };
    // Note: this account 404s on /subscribe/post-json; /subscribe/post honours the
    // `c` callback param and returns the same JSONP payload.
    script.src = `${MC.baseUrl.replace(/\/+$/, '')}/subscribe/post?${params.toString()}`;
    document.body.appendChild(script);
  });
}

/**
 * Hero video — served from the same origin, so no CORS or range-request concerns.
 * Set to '' to fall back to the still image alone.
 *
 * The clip is a slow push-in whose first frame matches /mhs/hero.jpg, so it plays
 * once and holds on its final frame rather than looping (looping would snap back
 * from the tight framing to the wide one every few seconds).
 *
 * 696 KB: H.264 1280x724, 1.41 Mbps, no audio, faststart.
 */
const HERO_VIDEO = '/mhs/hero.mp4';

const DEFAULT_IMAGES = {
  hero: '/mhs/hero.jpg',
  collect: '/mhs/collect.jpg',
  curate: '/mhs/curate.jpg',
  create: '/mhs/create.jpg',
  storytime: '/mhs/storytime.jpg',
};

const C = {
  ink: '#1c1c1c', cream: '#f7f3ec', paper: '#fff', gold: '#8a6d3b', goldDk: '#6f5527',
  muted: '#6b6459', line: '#e6ddcf', shadow: '0 20px 60px rgba(28,28,28,.12)',
  serif: "'Cormorant Garamond',Georgia,serif", sans: "'Inter',Helvetica,Arial,sans-serif",
};

const STEPS = [
  { day: 'Day 1 · Consultation', h: 'We map your story', p: 'An unhurried conversation about your life, your people, and the scope of the project. You leave with a clear plan and timeline.', fee: '$1,500 deposit' },
  { day: 'Days 2–10 · Collection', h: 'Every memory, digitized', p: 'I collect and carefully digitize your photos, documents, and materials. Deliverable: a complete digital archive.', fee: '$1,500' },
  { day: 'Days 11–20 · Curation', h: 'Your story takes shape', p: 'I organize everything into a narrative flow and draft your life-story book. Deliverable: a draft for your review.', fee: '$1,500' },
  { day: 'Days 21–30 · Creation', h: 'A finished heirloom', p: 'I incorporate your feedback and add the finishing touches. Deliverable: a beautifully bound book and digital archive.', fee: '$500 revisions + $1,000 on delivery' },
];

const CARDS = [
  { n: '01', h: 'Collect', p: 'Together we gather your photos, documents, and videos; digitizing every piece with archival care so nothing is lost.' },
  { n: '02', h: 'Curate', p: 'We shape your memories into a cohesive narrative, complete with the anecdotes and insights only you can tell.' },
  { n: '03', h: 'Create', p: 'We produce a beautifully bound book and digital archive, ready to share and to pass down for generations.' },
];

const QUESTIONS = [
  { key: 'goal', label: '1. What would you love to create?', opts: [
    { v: 'book', t: 'A bound life-story book' }, { v: 'video', t: 'A storytime video memoir' },
    { v: 'both', t: 'Both book & video' }, { v: 'ongoing', t: 'Ongoing preservation' } ] },
  { key: 'volume', label: '2. How much material are we working with?', opts: [
    { v: 'light', t: 'A few boxes' }, { v: 'medium', t: 'A closet full' }, { v: 'heavy', t: 'Decades, across the family' } ] },
  { key: 'when', label: '3. What pace feels right?', opts: [
    { v: 'project', t: 'One focused 30-day project' }, { v: 'retainer', t: 'Steady, month by month' } ] },
];

function estimate({ goal, volume, when }) {
  const volLabel = { light: 'a few boxes', medium: 'a closet full', heavy: 'decades of family material' }[volume];
  if (when === 'retainer' || goal === 'ongoing') {
    const monthly = volume === 'heavy' ? '$1,650/mo' : '$825/mo';
    const months = volume === 'light' ? '2–3' : volume === 'medium' ? '3–5' : '6+';
    return {
      title: 'A monthly retainer partnership',
      body: `With ${volLabel}, steady month-by-month work lets us do this carefully without rushing. Based on your goal, we’d likely work together for about ${months} months.`,
      est: monthly, note: `Estimated ${months} months · final scope confirmed at your consultation.`,
    };
  }
  let low = 6000, high = 6000, parts = ['the full 30-day book project ($6,000)'], title = 'The done-for-you life-story book';
  if (goal === 'video') { low = 450; high = 1200; parts = ['a storytime video memoir']; title = 'A storytime video memoir'; }
  if (goal === 'both') { low = 6450; high = 7200; parts.push('a storytime video (from $450)'); }
  if (volume === 'medium') high += 600;
  if (volume === 'heavy') high += 1500;
  const est = low === high ? `$${low.toLocaleString()}` : `$${low.toLocaleString()}–$${high.toLocaleString()}`;
  return {
    title,
    body: `For ${volLabel}, I’d recommend ${parts.join(' plus ')}. Heavier collections may add copy photography or extra production time.`,
    est, note: 'Starting estimate · begins with a $1,500 consultation deposit. Final quote confirmed together.',
  };
}

function useReveal() {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setShown(true); io.unobserve(el); }
    }, { threshold: 0.18 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return [ref, shown];
}

/**
 * Hero video loading.
 *
 * The still (IMG.hero) stays the header's CSS background, so it paints immediately
 * and remains the LCP element. The video's first frame is that same still, so when
 * it fades in the swap is invisible.
 *
 * The video is skipped entirely for reduced-motion users and on save-data/slow
 * connections, and is only fetched once the page has finished loading so it never
 * competes with critical resources.
 */
function useHeroVideo(src) {
  const [load, setLoad] = useState(false);

  useEffect(() => {
    if (!src) return undefined;

    const reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const conn = navigator.connection;
    const thrifty = conn && (conn.saveData || /(^|-)2g$/.test(conn.effectiveType || ''));
    if (reduced || thrifty) return undefined;

    const start = () => setLoad(true);
    if (document.readyState === 'complete') {
      const id = setTimeout(start, 200);
      return () => clearTimeout(id);
    }
    window.addEventListener('load', start, { once: true });
    return () => window.removeEventListener('load', start);
  }, [src]);

  return load;
}

/** How long the hero must stay out of view before the clip rewinds. */
const HERO_REPLAY_DELAY = 1500;

/**
 * Replays the hero clip when the visitor scrolls back to it.
 *
 * The rewind is deferred by HERO_REPLAY_DELAY so that scrolling quickly past the
 * hero, or overshooting and coming straight back, leaves it alone — only a
 * deliberate departure re-arms it. Playback then resumes when the hero is at
 * least half visible again, so it does not restart on a sliver peeking in.
 */
function useHeroReplay(heroRef, videoRef, enabled) {
  useEffect(() => {
    const hero = heroRef.current;
    if (!enabled || !hero || typeof IntersectionObserver === 'undefined') return undefined;

    let timer = null;
    let armed = false; // rewound, waiting to replay

    const io = new IntersectionObserver((entries) => {
      const entry = entries[entries.length - 1];

      if (entry.intersectionRatio === 0) {
        if (timer) return; // a rewind is already pending
        timer = setTimeout(() => {
          timer = null;
          const v = videoRef.current;
          if (!v) return;
          v.pause();
          v.currentTime = 0;
          armed = true;
        }, HERO_REPLAY_DELAY);
        return;
      }

      // Back in view: cancel any pending rewind.
      if (timer) { clearTimeout(timer); timer = null; }

      if (armed && entry.intersectionRatio >= 0.5) {
        armed = false;
        const v = videoRef.current;
        if (v) { const p = v.play(); if (p && p.catch) p.catch(() => {}); }
      }
    }, { threshold: [0, 0.5] });

    io.observe(hero);
    return () => { if (timer) clearTimeout(timer); io.disconnect(); };
  }, [heroRef, videoRef, enabled]);
}

function Reveal({ children, style }) {
  const [ref, shown] = useReveal();
  return (
    <div ref={ref} style={{
      opacity: shown ? 1 : 0, transform: shown ? 'none' : 'translateY(36px)',
      transition: 'opacity .9s ease, transform .9s ease', ...style,
    }}>{children}</div>
  );
}

const MyHistoryStudio = ({ bookLink = '/contact', onLead, images = {}, videoSrc = HERO_VIDEO }) => {
  const IMG = { ...DEFAULT_IMAGES, ...images };
  const videoLoad = useHeroVideo(videoSrc);
  const heroRef = useRef(null);
  const videoRef = useRef(null);
  useHeroReplay(heroRef, videoRef, videoLoad);
  const [answers, setAnswers] = useState({ goal: null, volume: null, when: null });
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle | sending | sent | error
  const [error, setError] = useState('');
  const resultRef = useRef(null);

  const complete = answers.goal && answers.volume && answers.when;
  const rec = complete ? estimate(answers) : null;

  useEffect(() => {
    if (complete && resultRef.current) resultRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [complete]);

  const pick = (key, v) => setAnswers((a) => ({ ...a, [key]: v }));

  // Readable version of the estimator answers, e.g.
  // "A bound life-story book / A closet full / One focused 30-day project"
  const answerPath = QUESTIONS
    .map((q) => { const o = q.opts.find((x) => x.v === answers[q.key]); return o ? o.t : '—'; })
    .join(' / ');

  const submit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    setError('');
    try {
      if (onLead) await onLead({ name, email, path: answerPath });
      else await subscribeToMailchimp({ email, name, path: answerPath });
      setStatus('sent');
    } catch (err) {
      setError(err.message);
      setStatus('error');
    }
  };

  const eyebrow = { fontFamily: C.sans, fontSize: 12, letterSpacing: '.28em', textTransform: 'uppercase', color: C.gold, fontWeight: 500, marginBottom: 18 };
  const wrap = { maxWidth: 1040, margin: '0 auto', padding: '0 24px' };
  const h2 = { fontFamily: C.serif, fontWeight: 500, lineHeight: 1.1, margin: 0, fontSize: 'clamp(34px,5vw,56px)' };

  return (
    <div className="my-history-studio">
      <Header />

      <div style={{ background: C.cream, color: C.ink, fontFamily: C.sans, fontWeight: 300, lineHeight: 1.6, overflowX: 'hidden' }}>
        {/* hero */}
        <header ref={heroRef} style={{ position: 'relative', minHeight: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center', padding: '120px 24px 90px', backgroundImage: `url(${IMG.hero})`, backgroundSize: 'cover', backgroundPosition: 'center', color: '#fff', overflow: 'hidden' }}>
          {videoLoad && (
            <video
              ref={videoRef}
              src={videoSrc}
              poster={IMG.hero}
              autoPlay
              muted
              playsInline
              preload="auto"
              aria-hidden="true"
              tabIndex={-1}
              // No fade needed: the poster, the header background and the video's
              // first frame are all the same image, so there is nothing to hide.
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', zIndex: 0 }}
            />
          )}
          {/* gradient sits above the still and the video so the headline stays legible either way */}
          <div aria-hidden="true" style={{ position: 'absolute', inset: 0, zIndex: 1, pointerEvents: 'none', background: 'linear-gradient(180deg, rgba(28,28,28,.34) 0%, rgba(28,28,28,.28) 45%, rgba(28,28,28,.66) 100%)' }} />
          <div style={{ position: 'relative', zIndex: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div style={{ ...eyebrow, color: '#e7cfa0' }}>My History Studio</div>
            <h1 style={{ fontFamily: C.serif, fontWeight: 500, lineHeight: 1.08, margin: 0, fontSize: 'clamp(44px,8vw,92px)', maxWidth: '15ch', color: '#fff', textShadow: '0 2px 30px rgba(0,0,0,.35)' }}>Your family's story deserves more than a shoebox.</h1>
            <div style={{ fontFamily: C.serif, fontStyle: 'italic', fontSize: 'clamp(20px,3vw,28px)', color: '#f3ece0', marginTop: 26, maxWidth: '26ch', textShadow: '0 2px 20px rgba(0,0,0,.4)' }}>"I turn those boxes of memories into a story your family can share."</div>
          </div>
        </header>

        {/* intro */}
        <section style={{ padding: '110px 0' }}>
          <Reveal style={{ ...wrap, textAlign: 'center', maxWidth: 760 }}>
            <div style={eyebrow}>A done-for-you legacy service</div>
            <h2 style={h2}>The memories are already yours. Let's make them&nbsp;last.</h2>
            <p style={{ color: C.muted, fontSize: 18, maxWidth: 640, margin: '16px auto 0' }}>You already trusted me to capture your family's moments. <strong style={{ fontWeight: 600 }}>My History Studio</strong> is the next chapter, a done-for-you legacy service that gathers the photos, documents, and voices forgotten across closets and cupboard drawers, and turns them into one beautifully bound book and digital archive.</p>
          </Reveal>
        </section>

        {/* statement */}
        <section style={{ background: C.ink, color: '#fff', textAlign: 'center', padding: '120px 24px' }}>
          <Reveal>
            <h2 style={{ fontFamily: C.serif, fontWeight: 500, fontStyle: 'italic', fontSize: 'clamp(34px,6vw,68px)', margin: 0, lineHeight: 1.1 }}>This is not a photo book.<br /><span style={{ color: '#d9c298' }}>It's a record of the story of a family's history.</span></h2>
          </Reveal>
        </section>

        {/* 3 C's */}
        <section style={{ padding: '110px 0' }}>
          <div style={wrap}>
            <Reveal style={{ textAlign: 'center', maxWidth: 640, margin: '0 auto 60px' }}>
              <div style={eyebrow}>How it works</div>
              <h2 style={h2}>Three steps, done for you</h2>
              <p style={{ color: C.muted, fontSize: 18, marginTop: 16 }}>You hand over the boxes. I handle the rest.</p>
            </Reveal>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(260px,1fr))', gap: 20 }}>
              {CARDS.map((c, i) => (
                <Reveal key={c.n}>
                  <Card {...c} img={[IMG.collect, IMG.curate, IMG.create][i]} />
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* timeline */}
        <section style={{ padding: '110px 0', background: '#efe7d8' }}>
          <div style={wrap}>
            <Reveal style={{ textAlign: 'center', maxWidth: 640, margin: '0 auto 60px' }}>
              <div style={eyebrow}>The 30-day journey</div>
              <h2 style={h2}>A clear path from shoebox to&nbsp;heirloom</h2>
              <p style={{ color: C.muted, fontSize: 18, marginTop: 16 }}>Every project follows the same considered rhythm — so you always know what's happening and what comes next.</p>
            </Reveal>
            <div style={{ position: 'relative', maxWidth: 760, margin: '0 auto' }}>
              <div style={{ position: 'absolute', left: 26, top: 8, bottom: 8, width: 2, background: C.line }} />
              {STEPS.map((s, i) => <Step key={i} {...s} />)}
            </div>
          </div>
        </section>

        {/* pricing */}
        <section style={{ padding: '110px 0' }}>
          <div style={wrap}>
            <Reveal style={{ textAlign: 'center', maxWidth: 640, margin: '0 auto 60px' }}>
              <div style={eyebrow}>Ways to work together</div>
              <h2 style={h2}>Prefer an ongoing partnership?</h2>
              <p style={{ color: C.muted, fontSize: 18, marginTop: 16 }}>A monthly retainer lets us work through decades of memories steadily.</p>
            </Reveal>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(260px,1fr))', gap: 22, maxWidth: 820, margin: '0 auto' }}>
              <Reveal><PriceCard h="Steady progress" amt="$825" per="/month" p="4 in-person hours or 6.5 production hours each month." /></Reveal>
              <Reveal><PriceCard h="Full immersion" amt="$1,650" per="/month" p="4 in-person hours and 6.5 production hours combined." /></Reveal>
            </div>
            <Reveal style={{ maxWidth: 820, margin: '34px auto 0', borderRadius: 16, overflow: 'hidden', position: 'relative' }}>
              <img src={IMG.storytime} alt="Filming a storytime video memoir" loading="lazy" style={{ width: '100%', height: 300, objectFit: 'cover', display: 'block' }} />
              <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg, rgba(28,28,28,.7) 0%, rgba(28,28,28,.25) 60%, rgba(28,28,28,0) 100%)', display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '0 40px' }}>
                <div style={{ ...eyebrow, color: '#e7cfa0', marginBottom: 8 }}>Storytime Video</div>
                <h3 style={{ fontFamily: C.serif, fontSize: 30, color: '#fff', margin: 0, maxWidth: '16ch' }}>Their voice, their stories — captured on film.</h3>
              </div>
            </Reveal>

            <Reveal style={{ maxWidth: 820, margin: '22px auto 0', background: '#efe7d8', borderRadius: 16, padding: '28px 34px' }}>
              <h3 style={{ fontFamily: C.serif, fontSize: 22, margin: '0 0 12px' }}>Add-on services</h3>
              <AddonRow b="Storytime Video" s="$450 first hour + $125/hr — I film you sharing your stories, edited into a video memoir." first />
              <AddonRow b="Copy Photography" s="$450 first hour + $125/hr — professional capture of oversized documents, art, and memorabilia." />
            </Reveal>
          </div>
        </section>

        {/* estimator */}
        <section id="mhs-start" style={{ padding: '110px 0', background: '#efe7d8' }}>
          <div style={wrap}>
            <Reveal style={{ background: C.ink, color: '#fff', borderRadius: 22, maxWidth: 820, margin: '0 auto', padding: '48px 44px', boxShadow: C.shadow }}>
              <h2 style={{ ...h2, color: '#fff', fontSize: 40, textAlign: 'center' }}>Find your path</h2>
              <p style={{ textAlign: 'center', color: '#b7ae9c', margin: '10px 0 30px' }}>Answer three quick questions for a personalized recommendation and estimate.</p>
              {QUESTIONS.map((q) => (
                <div key={q.key} style={{ marginBottom: 26 }}>
                  <div style={{ fontSize: 14, letterSpacing: '.04em', color: '#e7dfce', marginBottom: 12, fontWeight: 500 }}>{q.label}</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                    {q.opts.map((o) => {
                      const sel = answers[q.key] === o.v;
                      return (
                        <button key={o.v} type="button" onClick={() => pick(q.key, o.v)} style={{
                          border: `1px solid ${sel ? C.gold : '#4a453c'}`, background: sel ? C.gold : '#262218',
                          color: sel ? '#fff' : '#e7dfce', padding: '11px 18px', borderRadius: 40, cursor: 'pointer',
                          fontSize: 14, fontFamily: C.sans, transition: 'all .25s',
                        }}>{o.t}</button>
                      );
                    })}
                  </div>
                </div>
              ))}
              {rec && (
                <div ref={resultRef} style={{ marginTop: 14, background: '#262218', border: '1px solid #4a453c', borderRadius: 14, padding: 28 }}>
                  <h3 style={{ fontFamily: C.serif, color: '#fff', fontSize: 26, margin: '0 0 8px' }}>{rec.title}</h3>
                  <p style={{ color: '#c9c1b0', margin: 0 }}>{rec.body}</p>
                  <div style={{ fontFamily: C.serif, fontSize: 34, color: '#d9c298', margin: '14px 0 4px' }}>{rec.est}</div>
                  <div style={{ fontSize: 13, color: '#9a927f' }}>{rec.note}</div>
                </div>
              )}
            </Reveal>
          </div>
        </section>

        {/* CTA */}
        <section style={{ padding: '110px 0', textAlign: 'center', background: `linear-gradient(180deg,${C.cream} 0%,#efe7d8 100%)` }}>
          <Reveal style={wrap}>
            <div style={eyebrow}>Preserve your legacy today</div>
            <h2 style={h2}>Your story matters. Let's make sure it lasts.</h2>
            <p style={{ color: C.muted, fontSize: 18, maxWidth: '32ch', margin: '16px auto 34px' }}>Tell me a little about your project and I'll be in touch personally — or book your consultation now.</p>

            {status !== 'sent' ? (
              <form onSubmit={submit} style={{ maxWidth: 460, margin: '0 auto' }}>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" required style={inputStyle} />
                  <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="Email address" required style={inputStyle} />
                </div>
                <button type="submit" disabled={status === 'sending'} style={{ ...btn, background: C.ink, color: '#fff', width: '100%', marginTop: 12, opacity: status === 'sending' ? 0.6 : 1, cursor: status === 'sending' ? 'default' : 'pointer' }}>
                  {status === 'sending' ? 'Sending…' : 'Send me the details'}
                </button>
                {status === 'error' && (
                  <div role="alert" style={{ fontSize: 14, color: '#8a2b2b', background: '#f7e9e9', border: '1px solid #e2c3c3', borderRadius: 12, padding: '12px 16px', marginTop: 12 }}>
                    {error} You can also email <a href="mailto:jonah@kochfoto.com" style={{ color: '#8a2b2b', textDecoration: 'underline' }}>jonah@kochfoto.com</a> directly.
                  </div>
                )}
                <div style={{ fontSize: 12, color: C.muted, marginTop: 14 }}>No obligation. I'll reply personally within a day or two.</div>
              </form>
            ) : (
              <div style={{ background: '#fff', border: `1px solid ${C.gold}`, borderRadius: 16, padding: 26, maxWidth: 460, margin: '0 auto' }}>
                <h3 style={{ fontFamily: C.serif, fontSize: 26, margin: '0 0 8px' }}>Thank you — I'll be in touch.</h3>
                <p style={{ margin: 0, color: C.muted }}>I have your details and where you landed in the estimator. In the meantime, feel free to book your consultation below.</p>
              </div>
            )}

            <div style={{ margin: '22px 0 18px', color: C.muted, fontSize: 13, letterSpacing: '.1em', textTransform: 'uppercase' }}>— or —</div>
            {/* Internal route today; swap bookLink for a full https:// scheduler URL (Calendly etc.)
                and it automatically renders as an external link instead. */}
            {isExternal(bookLink) ? (
              <a href={bookLink} target="_blank" rel="noopener noreferrer" style={bookBtn}>Book your consultation</a>
            ) : (
              <Link to={bookLink} style={bookBtn}>Book your consultation</Link>
            )}
          </Reveal>
        </section>
      </div>

      <Footer />
    </div>
  );
};

const inputStyle = { flex: 1, minWidth: 180, padding: '15px 18px', border: `1px solid ${C.line}`, borderRadius: 40, fontFamily: C.sans, fontSize: 15, background: '#fff' };
const btn = { border: 'none', cursor: 'pointer', fontFamily: C.sans, fontSize: 15, fontWeight: 500, letterSpacing: '.03em', padding: '15px 30px', borderRadius: 40 };
const bookBtn = { ...btn, background: C.gold, color: '#fff', display: 'inline-block', textDecoration: 'none' };

function isExternal(href) {
  return /^(https?:)?\/\//i.test(href) || href.startsWith('mailto:');
}

function Card({ n, h, p, img }) {
  const [hover, setHover] = useState(false);
  return (
    <div onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)} style={{
      background: C.paper, border: `1px solid ${hover ? C.gold : C.line}`, borderRadius: 14, overflow: 'hidden',
      transform: hover ? 'translateY(-6px)' : 'none', boxShadow: hover ? C.shadow : 'none', transition: 'all .35s', height: '100%',
    }}>
      <div style={{ position: 'relative', width: '100%', aspectRatio: '4 / 3', overflow: 'hidden' }}>
        <img src={img} alt={h} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', transform: hover ? 'scale(1.05)' : 'none', transition: 'transform .6s ease' }} />
        <span style={{ position: 'absolute', left: 18, top: 12, fontFamily: C.serif, fontSize: 46, color: '#fff', lineHeight: 1, textShadow: '0 2px 12px rgba(0,0,0,.5)' }}>{n}</span>
      </div>
      <div style={{ padding: '24px 26px 30px' }}>
        <h3 style={{ fontFamily: C.serif, fontSize: 28, margin: '0 0 10px' }}>{h}</h3>
        <p style={{ color: C.muted, fontSize: 15, margin: 0 }}>{p}</p>
      </div>
    </div>
  );
}

function Step({ day, h, p, fee }) {
  const [ref, shown] = useReveal();
  return (
    <div ref={ref} style={{ position: 'relative', padding: '0 0 44px 76px', opacity: shown ? 1 : 0, transform: shown ? 'none' : 'translateY(24px)', transition: 'opacity .8s ease, transform .8s ease' }}>
      <span style={{ position: 'absolute', left: 16, top: 2, width: 22, height: 22, borderRadius: '50%', background: shown ? C.gold : C.cream, border: `3px solid ${C.gold}`, zIndex: 2, transform: shown ? 'scale(1.15)' : 'none', transition: 'all .4s' }} />
      <div style={{ fontSize: 12, letterSpacing: '.2em', textTransform: 'uppercase', color: C.gold, fontWeight: 600 }}>{day}</div>
      <h3 style={{ fontFamily: C.serif, fontSize: 26, margin: '6px 0 8px' }}>{h}</h3>
      <p style={{ color: C.muted, margin: '0 0 8px' }}>{p}</p>
      <span style={{ display: 'inline-block', fontFamily: C.serif, fontSize: 19, borderTop: `1px solid ${C.line}`, paddingTop: 8 }}>{fee}</span>
    </div>
  );
}

function PriceCard({ h, amt, per, p }) {
  return (
    <div style={{ background: C.paper, border: `1px solid ${C.line}`, borderRadius: 16, padding: 34, height: '100%' }}>
      <h3 style={{ fontFamily: C.serif, fontSize: 22, margin: '0 0 8px' }}>{h}</h3>
      <div style={{ fontFamily: C.serif, fontSize: 44 }}>{amt}<small style={{ fontSize: 16, color: C.muted }}>{per}</small></div>
      <p style={{ color: C.muted, fontSize: 15, margin: '10px 0 0' }}>{p}</p>
    </div>
  );
}

function AddonRow({ b, s, first }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, padding: '10px 0', borderTop: first ? 'none' : `1px solid ${C.line}` }}>
      <b style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{b}</b>
      <span style={{ color: C.muted, fontSize: 14, textAlign: 'right' }}>{s}</span>
    </div>
  );
}

export default MyHistoryStudio;
